import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { apiUrl } from "../../utils/api";
import { contactTypeLabel } from "../../utils/contactTypeLabel";

type Kundenverbindung = { id: number; name: string; memberCount: number };
type Member = { id: number; name: string; typ: string };

/**
 * Inline "Kundenverbindungen" view for the contact list. Lists all
 * Kundenverbindungen (ContactGroups of type 'Kundenverbindung') that have a
 * member in the caller's tenant. Each row is expandable; members are loaded
 * lazily on first expand via GET /contactgroup/:id/contacts — mirroring the
 * lazy-load pattern in RelationshipDetails.
 */
export const KundenverbindungList: React.FC = () => {
  const navigate = useNavigate();

  const [groups, setGroups] = useState<Kundenverbindung[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // null = not loaded, [] = loaded but empty, Member[] = loaded
  const [membersById, setMembersById] = useState<Record<number, Member[] | null>>({});
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetch(apiUrl("contactgroup/kundenverbindungen"))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`))))
      .then((data: unknown) => {
        if (cancelled) return;
        setGroups(Array.isArray(data) ? (data as Kundenverbindung[]) : []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMembers = (id: number) => {
    if (membersById[id] !== undefined || loadingIds.has(id)) return;
    setLoadingIds((prev) => new Set(prev).add(id));
    fetch(apiUrl(`contactgroup/${id}/contacts`))
      .then((r) => (r.ok ? r.json() : []))
      .then((data: unknown) => {
        setMembersById((prev) => ({
          ...prev,
          [id]: Array.isArray(data) ? (data as Member[]) : [],
        }));
      })
      .catch(() => {
        setMembersById((prev) => ({ ...prev, [id]: [] }));
      })
      .finally(() => {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      });
  };

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        loadMembers(id);
      }
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!groups) return [];
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  return (
    <div style={{ padding: "8px 4px 24px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kundenverbindung suchen…"
          style={{
            flex: "1 1 220px",
            minWidth: 0,
            border: "1px solid #ccc",
            borderRadius: 6,
            padding: "8px 12px",
            fontSize: 14,
          }}
        />
        {groups && (
          <span style={{ color: "#616161", fontSize: 13 }}>
            {filtered.length} Kundenverbindung{filtered.length === 1 ? "" : "en"}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ padding: "16px 12px", color: "#616161" }}>Lädt…</div>
      ) : error ? (
        <div style={{ padding: "16px 12px", color: "#a4262c" }}>
          Kundenverbindungen konnten nicht geladen werden.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: "16px 12px", color: "#616161" }}>
          {search.trim()
            ? "Keine Kundenverbindung gefunden."
            : "Keine Kundenverbindungen vorhanden."}
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: 6,
            overflow: "hidden",
          }}
        >
          {filtered.map((g, idx) => {
            const isOpen = expanded.has(g.id);
            const members = membersById[g.id];
            const isLoadingMembers = loadingIds.has(g.id);
            return (
              <div
                key={g.id}
                style={{
                  borderTop: idx === 0 ? "none" : "1px solid #e0e0e0",
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(g.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(g.id);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 14px",
                    background: isOpen ? "#f5f3f7" : "#fafafa",
                    fontWeight: 600,
                  }}
                >
                  <span style={{ width: 12, display: "inline-block", color: "#4d2051" }}>
                    {isOpen ? "▾" : "▸"}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                    {g.name}
                  </span>
                  <span
                    style={{
                      fontWeight: 500,
                      color: "#4d2051",
                      background: "#ECE7F0",
                      borderRadius: 999,
                      padding: "2px 10px",
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {g.memberCount} Kontakt{g.memberCount === 1 ? "" : "e"}
                  </span>
                </div>

                {isOpen && (
                  <div style={{ background: "#fff" }}>
                    {isLoadingMembers || members === undefined ? (
                      <div style={{ padding: "10px 14px", color: "#616161" }}>Lädt…</div>
                    ) : members && members.length > 0 ? (
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: 14,
                        }}
                      >
                        <thead>
                          <tr style={{ textAlign: "left", color: "#616161" }}>
                            <th
                              style={{
                                padding: "6px 14px",
                                fontWeight: 600,
                                borderTop: "1px solid #e0e0e0",
                              }}
                            >
                              Name
                            </th>
                            <th
                              style={{
                                padding: "6px 14px",
                                fontWeight: 600,
                                borderTop: "1px solid #e0e0e0",
                              }}
                            >
                              Typ
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((m, i) => (
                            <tr
                              key={m.id}
                              onClick={() => navigate(`/kontakte/edit/${m.id}`)}
                              style={{
                                cursor: "pointer",
                                background: i % 2 === 1 ? "#f5f5f5" : "transparent",
                              }}
                            >
                              <td style={{ padding: "8px 14px" }}>{m.name}</td>
                              <td style={{ padding: "8px 14px" }}>{contactTypeLabel(m.typ)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ padding: "10px 14px", color: "#616161" }}>
                        Keine Kontakte in dieser Kundenverbindung.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KundenverbindungList;
